import { useEffect, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  FORCE_LOGOUT_EVENT,
  FORCE_LOGOUT_GRACE_MS,
  completeForceLogout,
  type ForceLogoutDetail,
} from '../services/api';

/**
 * Banner shown when an admin force-signs-out the current user
 * (Administration > Users > Force Sign-Out).
 *
 * The API layer detects the force_logout 401 and dispatches FORCE_LOGOUT_EVENT;
 * this only owns the visible countdown. The sign-out itself is guaranteed by a
 * backstop timer in api.ts, so it still happens if this never mounts.
 *
 * Deliberately a top banner rather than a blocking overlay: the countdown exists
 * so the user can save in-flight work, which an overlay would prevent.
 */
const ForceLogoutBanner: React.FC = () => {
  const [message, setMessage] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(FORCE_LOGOUT_GRACE_MS / 1000));

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ForceLogoutDetail>).detail;
      if (!detail) return;

      setMessage(detail.message);
      setSecondsLeft(Math.ceil((detail.graceMs ?? FORCE_LOGOUT_GRACE_MS) / 1000));
    };

    window.addEventListener(FORCE_LOGOUT_EVENT, handler);
    return () => window.removeEventListener(FORCE_LOGOUT_EVENT, handler);
  }, []);

  useEffect(() => {
    if (message === null) return;

    const interval = window.setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [message]);

  if (message === null) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed top-0 left-0 right-0 z-[9999] bg-red-600 dark:bg-red-700 text-white shadow-lg"
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{message}</div>
          <div className="text-sm text-red-100">
            {secondsLeft > 0
              ? `Save your work — signing out in ${secondsLeft}s`
              : 'Signing out…'}
          </div>
        </div>
        <button
          type="button"
          onClick={completeForceLogout}
          className="flex-shrink-0 rounded border border-white/60 px-3 py-1.5 text-sm font-medium hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white"
        >
          Sign out now
        </button>
      </div>
    </div>
  );
};

export default ForceLogoutBanner;
